import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';
import type { CyberpunkPhoto } from '../api/types';
import CyberpunkPhotoCard from '../components/modules/CyberpunkPhotoCard';
import CyberpunkLightbox from '../components/modules/CyberpunkLightbox';
import ScanlineOverlay from '../components/layout/ScanlineOverlay';
import ParticleBackground from '../components/modules/ParticleBackground';
import styles from './CyberpunkGallery.module.css';

type LoadState = 'loading' | 'ready' | 'error';

/**
 * dev 环境固定展示的静态日记样例（接口为空或不可达时兜底），
 * 画面引用 public/assets/photos/ 既有图片；id = -1 标记非真实记录。
 */
const DEV_SAMPLE_PHOTO: CyberpunkPhoto = {
  id: -1,
  cos_key: 'dev/sample/first-light.jpg',
  cos_url: '/assets/photos/photo1.jpg',
  filename: 'dev_sample.jpg',
  title: 'FIRST LIGHT',
  description:
    '【DEV 样例】夜色降临时，城市的第一盏霓虹亮起。\n\n' +
    '这条记录是开发环境注入的静态日记数据——用于在没有 COS 配置、没有数据库记录时预览「照片日记」的完整形态：\n' +
    '点击卡片打开画面与文案，管理端（/admin → 图库）可上传真实记录。',
  diary_date: '2026-09-20',
  width: 1920,
  height: 1080,
  size_bytes: 0,
  mime_type: 'image/jpeg',
  sort_order: 0,
  status: 'active',
  source: 'manual',
  uploaded_by: 'dev',
  created_at: '2026-09-20T00:00:00Z',
  updated_at: '2026-09-20T00:00:00Z',
};

const NODES_PER_BATCH = 10;

interface TimelineNode {
  date: string;
  side: 'left' | 'right';
  items: { photo: CyberpunkPhoto; index: number; delayIdx: number }[];
}

interface NodeGeometry {
  lineX: number;
  stubX: number;
  y: number;
  top: number;
  bottom: number;
}

const dateOf = (photo: CyberpunkPhoto) =>
  photo.diary_date || photo.created_at.slice(0, 10);

export default function CyberpunkGallery() {
  const [photos, setPhotos] = useState<CyberpunkPhoto[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<CyberpunkPhoto | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [state, setState] = useState<LoadState>('loading');
  const [visibleBatches, setVisibleBatches] = useState(1);

  const timelineRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadPhotos = useCallback(async () => {
    setState('loading');
    try {
      const data = await api.listCyberpunkPhotos();
      // dev 环境：接口成功但记录为空时，注入静态样例保证始终有展示
      setPhotos(import.meta.env.DEV && data.length === 0 ? [DEV_SAMPLE_PHOTO] : data);
      setState('ready');
    } catch {
      // dev 环境：接口不可达（如后端未启动/COS 未配置）同样回退静态样例
      if (import.meta.env.DEV) {
        setPhotos([DEV_SAMPLE_PHOTO]);
        setState('ready');
      } else {
        setState('error');
      }
    }
  }, []);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, [loadPhotos]);

  const allNodes = useMemo<TimelineNode[]>(() => {
    const buckets = new Map<string, { photo: CyberpunkPhoto; index: number }[]>();
    photos.forEach((photo, index) => {
      const key = dateOf(photo);
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.push({ photo, index });
      } else {
        buckets.set(key, [{ photo, index }]);
      }
    });
    const dates = [...buckets.keys()].sort((a, b) => b.localeCompare(a));
    return dates.map((date, gi) => ({
      date,
      side: (gi % 2 === 0 ? 'left' : 'right') as TimelineNode['side'],
      items: (buckets.get(date) ?? []).map((it) => ({ ...it, delayIdx: 0 })),
    }));
  }, [photos]);

  const batches = useMemo<TimelineNode[][]>(() => {
    const out: TimelineNode[][] = [];
    for (let i = 0; i < allNodes.length; i += NODES_PER_BATCH) {
      out.push(allNodes.slice(i, i + NODES_PER_BATCH));
    }
    return out;
  }, [allNodes]);

  const visibleCount = Math.min(visibleBatches, batches.length);

  const visibleNodes = useMemo<TimelineNode[]>(() => {
    const out: TimelineNode[] = [];
    for (let b = 0; b < visibleCount; b++) {
      let delayIdx = 0;
      for (const node of batches[b]) {
        out.push({
          ...node,
          items: node.items.map((it) => ({ ...it, delayIdx: delayIdx++ })),
        });
      }
    }
    return out;
  }, [batches, visibleCount]);

  const hasMore = visibleCount < batches.length;

  const loadMore = useCallback(() => {
    setVisibleBatches((v) => Math.min(v + 1, batches.length));
  }, [batches.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!hasMore || !el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleBatches((v) => Math.min(v + 1, batches.length));
        }
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, batches.length]);

  useEffect(() => {
    const draw = () => {
      const container = timelineRef.current;
      const svg = svgRef.current;
      const path = pathRef.current;
      if (!container || !svg || !path) return;

      const items = Array.from(
        container.querySelectorAll<HTMLElement>('[data-timeline-item]'),
      );
      if (items.length === 0) {
        path.setAttribute('d', '');
        return;
      }

      const sRect = svg.getBoundingClientRect();
      const W = sRect.width;
      if (W <= 0) return;
      svg.setAttribute('viewBox', `0 0 ${W} ${sRect.height}`);

      const narrow = window.innerWidth <= 768;
      const LINE = narrow ? 24 : 50;

      const nodesGeo: NodeGeometry[] = [];
      items.forEach((item) => {
        const diamond = item.querySelector<HTMLElement>('[data-diamond]');
        const cards = item.querySelector<HTMLElement>('[data-cards]');
        if (!diamond || !cards) return;
        const isLeft = narrow || item.dataset.side === 'left';
        const dRect = diamond.getBoundingClientRect();
        const gRect = cards.getBoundingClientRect();
        const iRect = item.getBoundingClientRect();
        nodesGeo.push({
          lineX: isLeft ? LINE : W - LINE,
          stubX: isLeft
            ? gRect.left - sRect.left
            : gRect.right - sRect.left,
          y: dRect.top - sRect.top + dRect.height / 2,
          top: iRect.top - sRect.top,
          bottom: iRect.bottom - sRect.top,
        });
      });

      let d = '';
      nodesGeo.forEach((n, i) => {
        const p = nodesGeo[i - 1];
        if (p) {
          const midY = (p.bottom + n.top) / 2;
          d += ` M ${p.lineX} ${p.y} L ${p.lineX} ${midY} L ${n.lineX} ${midY} L ${n.lineX} ${n.y}`;
        }
        d += ` M ${n.lineX} ${n.y} L ${n.stubX} ${n.y}`;
      });
      path.setAttribute('d', d.trim());
    };

    draw();
    const raf = requestAnimationFrame(draw);
    const cardCount = visibleNodes.reduce((n, node) => n + node.items.length, 0);
    const animEnd = setTimeout(draw, cardCount * 60 + 700);
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    const ro =
      typeof ResizeObserver !== 'undefined' && timelineRef.current
        ? new ResizeObserver(() => draw())
        : null;
    if (ro && timelineRef.current) ro.observe(timelineRef.current);
    if (document.fonts) document.fonts.ready.then(() => draw());
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(animEnd);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [visibleNodes, loaded]);

  const visiblePhotos = visibleNodes.reduce(
    (n, node) => n + node.items.length,
    0,
  );

  return (
    <div className={styles.page}>
      <ScanlineOverlay />
      <ParticleBackground />

      {/* Header */}
      <header className={styles.header}>
        <div className={styles.titleWrapper}>
          <h1 className={styles.title} data-text="CYBER GALLERY">
            <span className={styles.glitchText} data-text="CYBER GALLERY">CYBER GALLERY</span>
          </h1>
          <div className={styles.titleLine}>
            <span className={styles.lineAccent} />
            <span className={styles.lineText}>// ARCHIVE SYSTEM v3.0 // COS LINK</span>
            <span className={styles.lineAccent} />
          </div>
        </div>

        <div className={styles.metaRow}>
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>STATUS</span>
            <span className={`${styles.metaValue} ${styles.pulseGreen}`}>
              ● {state === 'loading' ? 'SYNCING' : state === 'error' ? 'LINK ERROR' : 'OPERATIONAL'}
            </span>
          </span>
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>RECORDS</span>
            <span className={styles.metaValue}>{photos.length} ARCHIVED</span>
          </span>
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>THREAT LEVEL</span>
            <span className={styles.metaValue}>
              <span className={styles.threatIndicator} />
              MODERATE
            </span>
          </span>
        </div>
      </header>

      {/* Photo Timeline */}
      {state === 'loading' && (
        <main className={styles.grid}>
          <div className={styles.hudNotice}>▚▚ ESTABLISHING COS UPLINK...</div>
        </main>
      )}

      {state === 'error' && (
        <main className={styles.grid}>
          <div className={styles.hudNotice}>
            [!] DATA LINK FAILED — 记录库不可达
            <button className={styles.retryBtn} onClick={loadPhotos}>[ RETRY SYNC ]</button>
          </div>
        </main>
      )}

      {state === 'ready' && photos.length === 0 && (
        <main className={styles.grid}>
          <div className={styles.hudNotice}>// NO RECORDS FOUND — 档案为空，等待上传</div>
        </main>
      )}

      {state === 'ready' && photos.length > 0 && (
        <main>
          <div
            ref={timelineRef}
            className={`${styles.timeline} ${loaded ? styles.loaded : ''}`}
          >
            <svg ref={svgRef} className={styles.snakeSvg} aria-hidden="true">
              <path ref={pathRef} className={styles.snakePath} d="" />
            </svg>

            {visibleNodes.map((node) => (
              <div
                key={node.date}
                className={`${styles.timelineItem} ${node.side === 'left' ? styles.left : styles.right}`}
                data-timeline-item=""
                data-side={node.side}
                style={
                  {
                    '--anchor-top': node.side === 'left' ? '28%' : '72%',
                  } as CSSProperties
                }
              >
                <div className={styles.nodeContent}>
                  <span className={styles.dateLabel}>{node.date}</span>
                  <div className={styles.nodeDiamond} data-diamond="" />
                  <div className={styles.cardsGroup} data-cards="">
                    {node.items.map(({ photo, index, delayIdx }) => (
                      <div
                        key={photo.id}
                        className={`${styles.gridItem} ${styles.timelineCard}`}
                        style={{ animationDelay: `${delayIdx * 60}ms` }}
                      >
                        <CyberpunkPhotoCard
                          photo={photo}
                          index={index}
                          onClick={() => setSelectedPhoto(photo)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <>
              <div ref={sentinelRef} className={styles.sentinel} />
              <div className={styles.loadMoreRow}>
                <button
                  type="button"
                  className={styles.loadMoreBtn}
                  onClick={loadMore}
                >
                  [ SYNC MORE ]
                </button>
                <span className={styles.loadMoreInfo}>
                  {visibleNodes.length} / {allNodes.length} DATES · {visiblePhotos} / {photos.length} LOGS
                </span>
              </div>
            </>
          )}
        </main>
      )}

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerLine}>
          <span className={styles.footerDot} />
          <span className={styles.footerText}>SYSTEM ONLINE</span>
          <span className={styles.footerDot} />
        </div>
        <div className={styles.footerBarcode}>
          {Array.from({ length: 40 }, (_, i) => (
            <span key={i} className={styles.footerBar} style={{ height: `${Math.random() * 8 + 4}px` }} />
          ))}
        </div>
      </footer>

      {/* Lightbox */}
      {selectedPhoto && (
        <CyberpunkLightbox
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </div>
  );
}
